export type FetchErrorMeta = {
  method: string;
  endpoint: string;
};

type QueuedFetchError = FetchErrorMeta & {
  status: number;
  statusText: string;
  paired: boolean;
};

const byObject = new WeakMap<object, FetchErrorMeta>();
const pairedTo = new WeakMap<object, FetchErrorMeta>();
const entries: QueuedFetchError[] = [];

export function rememberFetchError(error: object, meta: FetchErrorMeta): void {
  byObject.set(error, meta);
  if (error instanceof Response) {
    entries.push({
      method: meta.method,
      endpoint: meta.endpoint,
      status: error.status,
      statusText: error.statusText,
      paired: false,
    });
    if (entries.length > 20) {
      const unpaired = entries.filter((entry) => !entry.paired).slice(-20);
      entries.length = 0;
      entries.push(...unpaired);
    }
  }
}

export function resolveFetchErrorMeta(error: object, status?: number, statusText?: string): FetchErrorMeta | undefined {
  const direct = byObject.get(error);
  if (direct) return direct;

  const paired = pairedTo.get(error);
  if (paired) return paired;

  if (status === undefined) return undefined;

  const entry = findQueueEntry(status, statusText ?? '');
  if (!entry) return undefined;

  entry.paired = true;
  const meta = { method: entry.method, endpoint: entry.endpoint };
  pairedTo.set(error, meta);
  return meta;
}

export function clearFetchErrorMeta(): void {
  entries.length = 0;
}

function findQueueEntry(status: number, statusText: string): QueuedFetchError | undefined {
  let fallback: QueuedFetchError | undefined;
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (entry.paired || entry.status !== status) continue;
    if (entry.statusText === statusText) return entry;
    fallback ??= entry;
  }
  return fallback;
}
